import { useState, useEffect } from 'react'
import { getSession, saveSession, clearSession, exchangeToken } from '../api/client'
import type { Session } from '../types'

type Status = 'loading' | 'authenticated' | 'unauthenticated' | 'error'

interface UseSessionResult {
  session: Session | null
  status: Status
  error: string | null
  logout: () => void
}

export function useSession(magicToken: string | null): UseSessionResult {
  const [session, setSession] = useState<Session | null>(null)
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function init() {
      // 1. Magic token in URL — always exchange it, even if a cached session exists.
      //    The token carries a specific channelId that must be honoured.
      if (magicToken) {
        try {
          const s = await exchangeToken(magicToken)
          saveSession(s)
          setSession(s)
          setStatus('authenticated')
          // Remove token from URL without reload
          const url = new URL(window.location.href)
          url.searchParams.delete('token')
          window.history.replaceState({}, '', url.toString())
        } catch (err: unknown) {
          const apiErr = err as { status?: number }
          if (apiErr?.status === 410) {
            setError('This link has expired. Run /review-ops settings in Slack to get a new one.')
          } else if (apiErr?.status === 401) {
            setError('This link is invalid or has already been used.')
          } else {
            setError('Something went wrong. Please try again.')
          }
          setStatus('error')
        }
        return
      }

      // 2. No magic token — check for a valid cached session
      const cached = getSession()
      if (cached) {
        setSession(cached)
        setStatus('authenticated')
        return
      }

      // 3. No token, no cache
      setStatus('unauthenticated')
    }

    init()
  }, [magicToken])

  function logout() {
    clearSession()
    setSession(null)
    setStatus('unauthenticated')
  }

  return { session, status, error, logout }
}
